import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Drawer,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  GridView,
  MenuBook,
  CalendarMonth,
  Quiz,
  Assignment,
  BarChart,
  Groups,
  ChatBubbleOutline,
  ShowChart,
  MoreHoriz,
  Settings as SettingsIcon,
  Person,
  ExitToApp,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import NotificationBell from '../../../components/NotificationBell';
import edumetrixRoundLogo from '../../../assets/edumetrix-round-logo.png';
import { MQ, wrapSx } from './teacherTokens';

/**
 * Teacher-portal navigation shell — the mockup's `.topnav` / `.wrap` /
 * `.bottomnav` / `.sheet` / `.usermenu`.
 *
 * Replaces the shared left-drawer Layout for teacher routes only; Admin keeps
 * Layout.js untouched. Presentation and navigation only — auth and logout come
 * from context, notifications from the existing NotificationBell.
 */
const NAV = [
  { label: 'Dashboard', path: '/teacher/dashboard', icon: <GridView /> },
  { label: 'My Classes', path: '/teacher/classes', icon: <MenuBook /> },
  { label: 'Calendar', path: '/teacher/calendar', icon: <CalendarMonth /> },
  { label: 'Quick Tests', path: '/teacher/quick-test', icon: <Quiz /> },
  { label: 'Homework', path: '/teacher/homework', icon: <Assignment /> },
  { label: 'Attendance', path: '/teacher/attendance', icon: <BarChart /> },
  { label: 'Students', path: '/teacher/students', icon: <Groups /> },
  { label: 'Chat', path: '/teacher/chat', icon: <ChatBubbleOutline /> },
  { label: 'Reports', path: '/teacher/reports', icon: <ShowChart /> },
];

// `.bottomnav` — four primary destinations plus the "More" sheet trigger.
const BOTTOM = ['Dashboard', 'My Classes', 'Calendar', 'Chat'];
const BOTTOM_LABELS = { Dashboard: 'Home', 'My Classes': 'Classes' };
// Everything the bottom bar cannot fit lives in the `.sheet`.
const SHEET = ['Quick Tests', 'Homework', 'Attendance', 'Students', 'Reports'];

const TeacherLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const isTablet = useMediaQuery('(max-width:1024px)');
  const activeNavRef = useRef(null);

  // Nine destinations do not fit the mockup's 1240px bar on every desktop
  // width, so the nav scrolls. Keep the current page's tab in view rather than
  // leaving it off the edge.
  useEffect(() => {
    activeNavRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [location.pathname]);

  const displayName =
    user?.profile?.name || user?.profile?.teacherName || user?.name || user?.username || 'Teacher';
  const email = user?.profile?.email || user?.email || '';
  const firstName = displayName.split(' ')[0];
  const initial = displayName.charAt(0).toUpperCase();

  const isActive = (path) => location.pathname === path;
  const go = (path) => {
    setSheetOpen(false);
    setAnchorEl(null);
    navigate(path);
    window.scrollTo(0, 0);
  };

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/login');
  };

  // `.mnav` / `.mnav.active`
  const navButtonSx = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 14px',
    borderRadius: '9px',
    fontSize: '13.5px',
    fontWeight: active ? 700 : 600,
    whiteSpace: 'nowrap',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: '.15s',
    color: active ? 'primary.main' : 'text.secondary',
    bgcolor: active ? (t) => alpha(t.palette.primary.main, 0.13) : 'transparent',
    '& svg': { fontSize: 18 },
    '&:hover': {
      bgcolor: active
        ? (t) => alpha(t.palette.primary.main, 0.18)
        : (t) => t.palette.teacher.line2,
      color: active ? 'primary.main' : 'text.primary',
    },
    [MQ.phoneSm]: { fontSize: '13px' },
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      {/* ===== top nav ===== */}
      <Box
        component="nav"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          bgcolor: (t) => alpha(t.palette.background.paper, 0.88),
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            maxWidth: 1240,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '22px',
            padding: '0 28px',
            height: 64,
            // Tighten the logo/nav gutter before the nav has to start scrolling.
            '@media (max-width:1360px)': { gap: '12px', padding: '0 20px' },
            [MQ.tablet]: { padding: '0 18px', gap: '14px' },
            [MQ.phoneLg]: { padding: '0 14px', gap: '10px' },
          }}
        >
          {/* `.logo` */}
          <Box
            onClick={() => go('/teacher/dashboard')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '11px',
              flex: 'none',
              cursor: 'pointer',
            }}
          >
            <Box
              component="img"
              src={edumetrixRoundLogo}
              alt="EduMetrix"
              sx={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
            />
            <Typography component="span" sx={{ fontWeight: 800, fontSize: 19, color: 'text.primary' }}>
              Edu
              <Box component="span" sx={{ color: 'primary.main' }}>
                Metrix
              </Box>
            </Typography>
          </Box>

          {/* `.mainnav` — desktop only.
              `flex:1; min-width:0` makes this the element that gives way when
              the nine destinations do not fit: it scrolls horizontally (as the
              mockup's `overflow-x:auto` does) instead of running underneath the
              notification/avatar cluster. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              height: '100%',
              flex: 1,
              minWidth: 0,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              [MQ.tablet]: { display: 'none' },
            }}
          >
            {NAV.map((item) => (
              <Box
                key={item.path}
                component="button"
                type="button"
                ref={isActive(item.path) ? activeNavRef : undefined}
                onClick={() => go(item.path)}
                sx={{ background: 'none', ...navButtonSx(isActive(item.path)) }}
              >
                {item.icon}
                {item.label}
              </Box>
            ))}
          </Box>

          {/* `.topR` */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '4px', flex: 'none' }}>
            <NotificationBell userType={user?.userType} />
            <Tooltip title="Settings">
              <IconButton onClick={() => go('/teacher/settings')} aria-label="Settings">
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            {/* `.uname` — hidden below 760px */}
            <Typography
              sx={{
                fontSize: '13.5px',
                fontWeight: 600,
                m: '0 4px',
                color: 'text.primary',
                [MQ.phoneLg]: { display: 'none' },
              }}
            >
              {firstName}
            </Typography>
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ p: '2px' }}
              aria-label="Account menu"
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {initial}
              </Avatar>
            </IconButton>

            {/* `.udrop` */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { mt: 1, minWidth: 240 } } }}
            >
              <Box
                sx={{
                  p: '12px',
                  mb: '6px',
                  borderBottom: '1px solid',
                  borderColor: (t) => t.palette.teacher.line2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '11px',
                }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontWeight: 700,
                  }}
                >
                  {initial}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '14.5px' }} noWrap>
                    {displayName}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary' }} noWrap>
                    {email}
                  </Typography>
                </Box>
              </Box>
              <MenuItem onClick={() => go('/teacher/profile')}>
                <Person sx={{ fontSize: 18 }} /> Profile
              </MenuItem>
              <MenuItem onClick={() => go('/teacher/settings')}>
                <SettingsIcon sx={{ fontSize: 18 }} /> Settings
              </MenuItem>
              <Divider sx={{ my: '6px' }} />
              <MenuItem
                onClick={handleLogout}
                sx={{
                  color: 'error.main',
                  '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.15) },
                }}
              >
                <ExitToApp sx={{ fontSize: 18 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* ===== page content (`.wrap`) ===== */}
      <Box component="main" sx={wrapSx}>
        {children}
      </Box>

      {/* ===== bottom nav (`.bottomnav`) — tablet and below ===== */}
      {isTablet && (
        <Box
          component="nav"
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            height: 72,
            zIndex: 32,
            px: '4px',
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', height: '100%', maxWidth: 560, mx: 'auto' }}>
            {NAV.filter((n) => BOTTOM.includes(n.label)).map((item) => {
              const active = isActive(item.path);
              return (
                <BottomItem
                  key={item.path}
                  icon={item.icon}
                  label={BOTTOM_LABELS[item.label] || item.label}
                  active={active}
                  onClick={() => go(item.path)}
                />
              );
            })}
            <BottomItem
              icon={<MoreHoriz />}
              label="More"
              active={NAV.some((n) => SHEET.includes(n.label) && isActive(n.path))}
              onClick={() => setSheetOpen(true)}
            />
          </Box>
        </Box>
      )}

      {/* ===== "More" sheet (`.sheet`) ===== */}
      <Drawer
        anchor="bottom"
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '22px 22px 0 0',
              padding: '10px 12px 28px',
              maxHeight: '72vh',
              bgcolor: 'background.paper',
            },
          },
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 4,
            borderRadius: '4px',
            bgcolor: 'divider',
            margin: '8px auto 12px',
          }}
        />
        <Typography sx={{ fontWeight: 700, fontSize: '15px', padding: '0 12px 12px' }}>More</Typography>
        {NAV.filter((n) => SHEET.includes(n.label)).map((item) => (
          <Box
            key={item.path}
            component="button"
            type="button"
            onClick={() => go(item.path)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              width: '100%',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'start',
              padding: '13px',
              borderRadius: '12px',
              fontWeight: 600,
              fontSize: '14.5px',
              color: isActive(item.path) ? 'primary.main' : 'text.primary',
              '&:hover': { bgcolor: (t) => t.palette.teacher.line2 },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '11px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.13),
                color: 'primary.main',
                '& svg': { fontSize: 16 },
              }}
            >
              {item.icon}
            </Box>
            {item.label}
          </Box>
        ))}
      </Drawer>

      {/* ===== chat FAB (`.fab`) ===== */}
      {!isActive('/teacher/chat') && (
        <Box
          component="button"
          type="button"
          aria-label="Open chat"
          onClick={() => go('/teacher/chat')}
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            width: 56,
            height: 56,
            borderRadius: '16px',
            border: 'none',
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            zIndex: 25,
            transition: '.15s',
            boxShadow: (t) => `0 12px 26px -8px ${alpha(t.palette.primary.main, 0.55)}`,
            '& svg': { fontSize: 21 },
            '&:hover': { bgcolor: 'primary.dark', transform: 'translateY(-2px)' },
            [MQ.tablet]: { bottom: 90 },
          }}
        >
          <ChatBubbleOutline />
        </Box>
      )}
    </Box>
  );
};

// `.bn-item` — icon in a rounded pill that fills with the primary tint when active.
const BottomItem = ({ icon, label, active, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '3px',
      pt: '8px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: '10.5px',
      fontWeight: 600,
      color: active ? 'primary.main' : 'text.secondary',
      [MQ.phoneSm]: { fontSize: '9px' },
    }}
  >
    <Box
      sx={{
        width: 52,
        height: 30,
        borderRadius: '15px',
        display: 'grid',
        placeItems: 'center',
        transition: '.15s',
        bgcolor: active ? (t) => alpha(t.palette.primary.main, 0.13) : 'transparent',
        '& svg': { fontSize: 17 },
        [MQ.phoneSm]: { width: 44, height: 28 },
      }}
    >
      {icon}
    </Box>
    {label}
  </Box>
);

export default TeacherLayout;
